import { useState } from 'react';
import { api, humanise, type MapsCheck, type MatchCheck } from '../api';
import { PageHead, Panel, State } from '../ui';

const hhmm = (m: number | null) =>
  m == null ? '—' : `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

/**
 * The two questions support keeps getting, answered from live data:
 * "why are there no address suggestions?" and "why can't this rider see
 * that driver?".
 */
export function Checks() {
  return (
    <>
      <PageHead
        title="Checks"
        blurb="Live answers to the two commonest reports: no address suggestions, and a rider who cannot see a driver on their road."
      />
      <MapsPanel />
      <MatchPanel />
    </>
  );
}

function MapsPanel() {
  const [res, setRes] = useState<MapsCheck | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const run = async () => {
    setBusy(true);
    setErr(null);
    try {
      setRes(await api.mapsCheck());
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Check failed');
    } finally {
      setBusy(false);
    }
  };

  const cell = (p: { ok: boolean; ms: number; detail: string } | null) =>
    p === null ? <span className="muted">—</span> : (
      <span>
        <State tone={p.ok ? 'ok' : 'bad'}>{p.ok ? 'works' : 'fails'}</State>
        <small className="detail">{p.detail} · {p.ms} ms</small>
      </span>
    );

  return (
    <Panel title="Address search and pins" right={
      <button className="btn sm primary" disabled={busy} onClick={() => void run()}>{busy ? 'Checking…' : 'Run check'}</button>
    }>
      <div className="pad">
        <p className="hint">
          Calls each geocoder once from this server. The app uses the first that answers, so suggestions
          still appear while Google fails — but Google's error says what to fix in the Cloud console.
        </p>
        {err ? <p className="hint bad">{err}</p> : null}
        {res ? (
          <table className="checks">
            <thead><tr><th>Provider</th><th>Suggestions</th><th>Search</th><th>Pin → address</th></tr></thead>
            <tbody>
              {res.providers.map((p) => (
                <tr key={p.name}>
                  <td><strong>{p.name}</strong>{p.name === res.geocodeProvider ? <small className="detail">primary</small> : null}</td>
                  <td>{cell(p.autocomplete)}</td>
                  <td>{cell(p.search)}</td>
                  <td>{cell(p.reverse)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        {res?.providers.some((p) => p.name === 'google' && !(p.search.ok && p.autocomplete?.ok)) ? (
          <ul className="fixes">
            <li>The server key needs <strong>Geocoding API</strong> and <strong>Places API (New)</strong> enabled.</li>
            <li>If the error names an IP, add exactly that address to the key's IP restriction — the server's IPv6 address as well as its IPv4 one, if it has both.</li>
          </ul>
        ) : null}
      </div>
    </Panel>
  );
}

function MatchPanel() {
  const [phone, setPhone] = useState('');
  const [res, setRes] = useState<MatchCheck | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    setErr(null);
    setRes(null);
    try {
      setRes(await api.matchCheck(phone));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Check failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title="Why can't this rider see a driver?">
      <div className="pad">
        <form className="row" onSubmit={(e) => { e.preventDefault(); void run(); }}>
          <input type="tel" placeholder="Rider's phone" value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(-10))} />
          <button className="btn primary" disabled={busy || phone.length !== 10}>{busy ? 'Checking…' : 'Check'}</button>
        </form>
        {err ? <p className="hint bad">{err}</p> : null}
        {res?.routes.map((r) => (
          <div key={r.route.id} className="matchroute">
            <h3>
              {r.route.rider ?? 'Rider'} · {humanise(r.route.direction)} · leaves {hhmm(r.route.departMin)}
            </h3>
            <p className="hint">{r.route.pickupAddress} → {r.route.dropAddress}</p>
            {r.drivers.length === 0 ? <p className="hint">No active driver routes at all.</p> : (
              <table className="checks">
                <thead><tr><th>Driver (nearest first)</th><th>Offered?</th><th>What fails</th></tr></thead>
                <tbody>
                  {r.drivers.map((d) => {
                    const failing = d.checks.filter((c) => !c.ok);
                    return (
                      <tr key={d.rideId}>
                        <td>
                          <strong>{d.driver.name ?? '—'}</strong> <small className="detail">{d.driver.phone}</small>
                          <small className="detail">{d.route}</small>
                        </td>
                        <td>
                          {d.offered ? <State tone="ok">offered</State>
                            : failing.length ? <State tone="bad">no</State>
                            : <State tone="warn">no — detour or seats</State>}
                        </td>
                        <td>
                          {failing.length === 0 ? <span className="muted">{d.offered ? 'nothing' : 'passes every road filter; too long a detour, or full'}</span> : (
                            <ul className="fails">
                              {failing.map((c) => <li key={c.key}>{c.label}{c.detail ? <small className="detail">{c.detail}</small> : null}</li>)}
                            </ul>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </div>
    </Panel>
  );
}
